import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { SystemConfigurationSettingsService } from '../../services/system-configuration-settings.service';
import { HeaderComponent } from '../header/header.component';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-systemconfigurationsettings',
  standalone: true,
  imports: [HeaderComponent, FormsModule, CommonModule],
  templateUrl: './system.config.setting.component.html',
})
export class SystemConfigurationSettingsComponent implements OnInit {
  copyrightDetails: any;
  oldAllowCollectionDetails: any = {};
  allowCollectionDetails: any = {};
  message: string | null = null;
  errorMessage: string | null = null;
  showDependency: boolean = false;
  isLoading: boolean = false;
  isUpdating: boolean = false;

  constructor(
    private systemConfigurationSettingsService: SystemConfigurationSettingsService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    if (typeof window !== 'undefined') {
      const savedDetails = sessionStorage.getItem('copyrightDetails');
      if (savedDetails) {
        this.copyrightDetails = JSON.parse(savedDetails);
      }
    }
    this.activate();
  }

  activate(): void {
    this.getSystemConfigurationSettings("allow_collection_attributes");
  }

  resetMessages(): void {
    this.message = null;
    this.errorMessage = null;
    this.showDependency = false; // Hide warning if user toggles checkbox back and forth
  }

  getSystemConfigurationSettings(code: string): void {
    this.isLoading = true;
    this.systemConfigurationSettingsService.API.GetSystemConfigurationSettings(code)
      .subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response?.data && response.data.length > 0) {
            if (code === "allow_collection_attributes") {
              // Create deep copies to prevent reference sharing
              this.allowCollectionDetails = JSON.parse(JSON.stringify(response.data[0]));
              this.oldAllowCollectionDetails = JSON.parse(JSON.stringify(response.data[0]));
              this.cdr.detectChanges();
            }
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error("Fetch Error:", error);
        }
      });
  }

  // Improved comparison logic
  hasUpdateChange(newData: any, oldData: any): boolean {
    if (!newData || !oldData) return false;
    // Use Number() to ensure we aren't comparing "1" (string) to 1 (number)
    return Number(newData.yes_or_no) !== Number(oldData.yes_or_no);
  }

  checkAllowCollectionDependency(data: any): void {
    console.log('Comparison Check:', {
      newData: data.yes_or_no,
      oldData: this.oldAllowCollectionDetails.yes_or_no,
      hasChanged: this.hasUpdateChange(data, this.oldAllowCollectionDetails)
    });

    if (this.hasUpdateChange(data, this.oldAllowCollectionDetails)) {
      this.isUpdating = true;
      this.systemConfigurationSettingsService.API.DependencyCheck()
        .subscribe({
          next: (response: any) => {
            this.isUpdating = false;
            // If API returns 200 but body says 412
            if (response?.status === 412 || response?.data?.status === 412) {
              this.showDependency = true;
              this.cdr.detectChanges();
            } else {
              this.updateSystemConfigurationSettings(data);
            }
          },
          error: (error: any) => {
            this.isUpdating = false;
            // Check for the 412 status
            if (error.status === 412 || error.error?.type === "_check_dependency") {
              console.log('412 Dependency caught, showing note section.');
              this.showDependency = true;
              // DO NOT call update here. Let the user click "Proceed" in the HTML.
              this.cdr.detectChanges();
            } else {
              this.errorMessage = "Dependency check failed.";
              this.clearErrorAfterDelay();
              this.cdr.detectChanges();
            }
          }
        });
    } else {
      this.errorMessage = "Nothing to update.";
      setTimeout(() => {
        this.errorMessage = null;
        this.cdr.detectChanges(); // <-- Add this to clear it from the UI
      }, 3000);
      this.clearErrorAfterDelay();
    }
  }

  updateSystemConfigurationSettings(data: any): void {
    this.isUpdating = true;
    this.systemConfigurationSettingsService.API.UpdateSystemConfigurationSettings(data)
      .subscribe({
        next: (response: any) => {
          this.isUpdating = false;
          this.showDependency = false;
          this.message = response?.data?.message || "Settings updated successfully!";
          // Update the "old" data to match the new saved state
          this.oldAllowCollectionDetails = JSON.parse(JSON.stringify(data));
          this.cdr.detectChanges();

          setTimeout(() => {
            this.message = null;
            this.cdr.detectChanges(); // <-- Add this to clear it from the UI
          }, 3000);
        },
        error: (err: any) => {
          this.isUpdating = false;
          if (err.error.status === 412 || err.error?.type === "_check_dependency") {
            this.showDependency = true;
          }
          this.errorMessage = "Something went wrong during update!";

          setTimeout(() => {
            this.errorMessage = null;
            this.cdr.detectChanges(); // <-- Add this to clear it from the UI
          }, 3000);
          this.clearErrorAfterDelay();
        }
      });
  }

  private clearErrorAfterDelay(): void {
    setTimeout(() => { this.errorMessage = null; }, 3000);
  }
}